import type { ClrDecision } from './credit-limit-reviews'
import {
  RCL_MULTIPLIER_PAYMENT_TERMS,
  type RclMultiplierPaymentTerm,
} from './rcl-multiplier-mappings'

// Unrelated to the order-class (BAU / injection / unclassified) split of ZF2 invoices in `@repo/utils` (`classifyOrder`).

// ── Statuses ─────────────────────────────────────────────────────────────────────────────────
/**
 * The FUZE-632 lifecycle. Two loops share `draft`:
 *  - non-final: draft → sent_for_review → (reviewer approves / returns / rejects) → draft — the
 *    marketing back-and-forth. Each pass ends back in draft for export + next file version.
 *  - final: draft → final (48h SLA starts; reviewer_1 then every primary approver) → approved.
 * `approved → started → ended` are human pushes: SAP is keyed by hand, so nothing is date-driven —
 * the dates only power reminders and the "start due"/"ended — not stopped" derived badges.
 */
export const INITIATIVE_STATUSES = [
  'draft',
  'sent_for_review',
  'final',
  'approved',
  'started',
  'ended',
] as const
export type InitiativeStatus = (typeof INITIATIVE_STATUSES)[number]

/** In the review chain: a `pending_slot` is set and chain decisions apply. */
export const INITIATIVE_CHAIN_STATUSES = ['sent_for_review', 'final'] as const
export type InitiativeChainStatus = (typeof INITIATIVE_CHAIN_STATUSES)[number]

export function isInitiativeInChain(status: InitiativeStatus): status is InitiativeChainStatus {
  return (INITIATIVE_CHAIN_STATUSES as readonly InitiativeStatus[]).includes(status)
}

// ── List tabs ────────────────────────────────────────────────────────────────────────────────
export const INITIATIVE_LIST_TABS = ['current', 'draft', 'active', 'ended'] as const
export type InitiativeListTab = (typeof INITIATIVE_LIST_TABS)[number]

export const INITIATIVE_STATUSES_BY_TAB = {
  current: ['draft', 'sent_for_review', 'final', 'approved', 'started'],
  draft: ['draft'],
  active: ['started'],
  ended: ['ended'],
} as const satisfies Record<InitiativeListTab, readonly InitiativeStatus[]>

// ── Action history ───────────────────────────────────────────────────────────────────────────
/** Rows of `initiative_actions` — the container's timeline, mirroring mass CLR's action log. */
export const INITIATIVE_ACTION_TYPES = [
  'sent_for_review',
  'sent_final',
  'reviewed',
  'approved',
  'rejected',
  'sent_back',
  'started',
  'stopped',
] as const
export type InitiativeActionType = (typeof INITIATIVE_ACTION_TYPES)[number]

/** Chain decisions reuse the shared CLR verbs. */
export type InitiativeChainDecision = ClrDecision

// ── Diff ─────────────────────────────────────────────────────────────────────────────────────
/** What marketing did to a row (or a payer) between the previous file version and the latest. */
export const INITIATIVE_DIFF_STATES = ['added', 'modified', 'removed', 'unchanged'] as const
export type InitiativeDiffState = (typeof INITIATIVE_DIFF_STATES)[number]

// ── Injection decisions ──────────────────────────────────────────────────────────────────────
/**
 * The system verdict computed at import, stored as a reason KEY (translated client-side, never
 * prose). Order matters: the first matching reason is the stored one — inactivity beats deposits
 * beats payment history.
 */
export const INITIATIVE_INJECTION_REJECT_REASONS = [
  'inactive_erp',
  'security_deposit',
  'failed_payment',
] as const
export type InitiativeInjectionRejectReason = (typeof INITIATIVE_INJECTION_REJECT_REASONS)[number]

/** A CIS score is "good" strictly below this (the scale inverts intuition: 70 means bankruptcy). */
export const INITIATIVE_GOOD_CIS_MAX = 31

/** Failed payments older than this never auto-reject; accounts younger than the second are "new". */
export const INITIATIVE_FAILED_PAYMENT_WINDOW_MONTHS = 12
export const INITIATIVE_NEW_ACCOUNT_WINDOW_MONTHS = 6

/** Uplift buffer applied on (recommended limit + injection), per risk class. This is the default
 *  when an initiative has no stored mapping for the class; overrides are per initiative. */
export const INITIATIVE_DEFAULT_BUFFER_PERCENT = 30

/** Key accounts always calculate with the least-risky payment term (credit team rule, 2026-09-08:
 *  "no terms, key account" = N000 — these are big payers expected to pay on time). */
export const INITIATIVE_KEY_ACCOUNT_PAYMENT_TERM = 'N000'

// ── Import file ──────────────────────────────────────────────────────────────────────────────
/** Three required columns (2026-09-09 session): "the upload from marketing is the ERP, the
 *  sold-to, the dollar value, and the payment term request". */
export const INITIATIVE_CSV_HEADERS = ['sold_to_erp', 'initiative_amount', 'payment_term'] as const

/**
 * The requested payment term, normalized to the app's term enum. Accepts the ZTERM code itself
 * (`B007`, case-insensitive) or a bare/prefixed day count (`7`, `PAD7` → B007; `0`, `PAD0` →
 * N000) — the exact format marketing's TMND team will send is still being aligned, and this
 * covers both likely answers. Null = undecipherable (a row-level attention flag, never a block).
 */
export function normalizeInitiativePaymentTerm(raw: string): RclMultiplierPaymentTerm | null {
  const cleaned = raw.trim().toUpperCase()
  if (cleaned === '') return null
  const exact = RCL_MULTIPLIER_PAYMENT_TERMS.find((term) => term === cleaned)
  if (exact) return exact
  const digits = cleaned.replace(/\D/g, '')
  if (digits === '') return null
  const days = Number(digits)
  return (
    RCL_MULTIPLIER_PAYMENT_TERMS.find((term) => Number(term.replace(/\D/g, '')) === days) ?? null
  )
}

/**
 * The decision export back to marketing. The required import columns come first and adjacent so
 * the export re-imports as-is: the parser requires `INITIATIVE_CSV_HEADERS` and ignores the rest
 * of THIS list, while still rejecting truly unknown columns (the master-file guard).
 */
export const INITIATIVE_EXPORT_CSV_HEADERS = [
  'sold_to_erp',
  'initiative_amount', // the FILE value — marketing's number, what a re-import reads
  'payment_term', // the FILE-requested term — same round-trip contract as initiative_amount
  'injection_amount', // credit's decided amount — the signal marketing folds back manually
  'was_modified', // Y when the injected amount differs from the file value
  'decided_payment_term', // credit's decided term — marketing folds it into payment_term manually
  'terms_modified', // Y when the decided term differs from the file's requested term
  'payer_erp',
  'injection_decision',
  'decision_comment',
  'user_comment',
] as const

/** Row-level flags: never block an import, surfaced as "needs attention". */
export const INITIATIVE_ROW_ISSUES = [
  'unknownSoldTo',
  'noPayerMapping',
  'invalidAmount',
  'invalidPaymentTerm',
] as const
export type InitiativeRowIssue = (typeof INITIATIVE_ROW_ISSUES)[number]

/** Structural failures: nothing is imported. Double as i18n keys under
 *  `initiatives.create.parseErrors`. Duplicates are structural (unlike FUZE-631) because a
 *  duplicate sold-to would break the per-version unique key the diff joins on. */
export const INITIATIVE_PARSE_ERRORS = [
  'missingColumns',
  'unrecognizedColumns',
  'unreadableFile',
  'noValidRows',
  'duplicateSoldTos',
] as const
export type InitiativeParseError = (typeof INITIATIVE_PARSE_ERRORS)[number]

// ── Row edits ────────────────────────────────────────────────────────────────────────────────
export const INITIATIVE_ROW_PATCH_KINDS = [
  'setAmount',
  'resetToFile',
  'revertToRecommendation',
  'setUserComment',
  'setReviewed',
  'setInjectionDecision',
  'setPaymentTerm',
] as const
export type InitiativeRowPatchKind = (typeof INITIATIVE_ROW_PATCH_KINDS)[number]

// ── SLA ──────────────────────────────────────────────────────────────────────────────────────
/** Marking final starts this clock (re-stamped on each send-final). Tracking only — nothing
 *  transitions on breach. */
export const INITIATIVE_FINAL_SLA_HOURS = 48

// ── Business-rule errors ─────────────────────────────────────────────────────────────────────
// Emitted as a ZodIssue `params.type` so the frontend translates `initiatives.errors.<key>`.
export const INITIATIVE_ERRORS = [
  'initiativeNotEligiblePreparer',
  'initiativeEndBeforeStart',
  'initiativeNotDraft',
  'initiativeInvalidState',
  'initiativeRowsNotReviewed',
  'initiativeNotLatestVersion',
  'initiativeCannotDeleteOnlyVersion',
  'initiativeInjectionOverrideCommentRequired',
  'initiativeStartCommentRequired',
  'initiativeEndCommentRequired',
  'initiativeNoApproversConfigured',
  // The file failed structural parsing on submit; `/parse` reports the detail up front.
  'initiativeFileUnparsable',
] as const
export type InitiativeErrorType = (typeof INITIATIVE_ERRORS)[number]

export function isInitiativeErrorType(value: unknown): value is InitiativeErrorType {
  return (INITIATIVE_ERRORS as readonly unknown[]).includes(value)
}
